import React from "react"
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Divider,
  useTheme,
  IconButton,
  CloseIcon,
  Button,
  UnorderedList,
  UnorderedListItem,
  ListItemText,
  Link,
  Alert
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl, FormattedDate } from "react-intl"
import moment from "moment/moment"
import { useFlags } from "launchdarkly-react-client-sdk"
import { ExpenseState } from "#/utils/ExpenseState"
import { ExpenseType } from "#/utils/ExpenseType"
import { JourneyTypes } from "#/components/get-reimbursed/steps/ExpenseJourneyTypes"
import { Reimbursement } from "#/types/reimbursement"
import { getFileOnClick } from "#/components/views/reimbursements/upload/Attachments"
import { useInvoices } from "#/hooks/useInvoices"
import type { Invoice } from "#/hooks/useInvoices"
import {
  CardHistoryEntries,
  DirectPayHistoryEntries,
  OopHistoryEntries
} from "#/components/views/reimbursements/TransactionsDrawerHistoryEntries"
import { DirectPayAmounts } from "#/components/views/reimbursements/DirectPayAmounts"
import { CardAmounts } from "#/components/views/reimbursements/CardAmounts"
import { BenefitType, OutOfPocketMaxState, QmeState, Subsidies } from "#/lib/carrot-api/types/Subsidy"

interface TransactionDetailsDrawerProps {
  open: boolean
  onClose: () => void
  transaction: Reimbursement
  subsidies?: Subsidies | null
}

export const TransactionDetailsDrawer: React.FC<TransactionDetailsDrawerProps> = ({
  open,
  onClose,
  transaction,
  subsidies
}) => {
  const intl = useIntl()
  const theme = useTheme()
  const { transactionDetailsDrawerForOopAndCardEnabled } = useFlags()

  const invoiceExpenseId: number | null = (() => {
    if (!transaction) return null
    if (!transaction.hasBeenReconciled) return null
    return transaction.reconciliationRequestedExpenseId || transaction.expenseId
  })()

  const invoiceQueryResults = useInvoices(invoiceExpenseId ? [invoiceExpenseId] : [])
  const invoices: Invoice[] = invoiceQueryResults[0]?.data ?? []
  const openInvoice = invoices.find((inv) => inv.status === "open") ?? null

  if (!transaction) {
    return null
  }

  // Check expense type
  const isDirectPayExpense = transaction.expenseType === ExpenseType.DIRECT_PAY
  const isOopExpense = transaction.expenseType === ExpenseType.OOP
  const isCardExpense = transaction.expenseType === ExpenseType.CARD

  const showReconciliationAlert =
    openInvoice !== null &&
    ((isCardExpense && transaction.expenseState === ExpenseState.DENIED_RECONCILIATION_REQUESTED) ||
      (isOopExpense &&
        transaction.expenseState === ExpenseState.PAID &&
        !!transaction.reconciliationRequestedExpenseId))

  // Determine if drawer should be shown
  // DIRECTPAY always shows, OOP/CARD show when feature flag is enabled
  const shouldShowDrawer =
    isDirectPayExpense || (transactionDetailsDrawerForOopAndCardEnabled && (isOopExpense || isCardExpense))

  if (!shouldShowDrawer) {
    return null
  }

  const shouldShowFullContent = isDirectPayExpense
  const shouldShowOopCardContent = isOopExpense || isCardExpense

  // Only calculate these for DIRECTPAY expenses to avoid accessing properties that may not exist for OOP/CARD
  const serviceDateStringToDate = isDirectPayExpense ? moment(transaction.serviceDate).toDate() : null

  const getWhoReceivedCare = () => {
    switch (transaction.whoReceivedCare) {
      case "MEMBER":
        return intl.formatMessage({ defaultMessage: "Me" })
      case "LEGAL_PARTNER":
        return intl.formatMessage({ defaultMessage: "My legal spouse or tax-dependent partner" })
      case "NON_MARRIED_PARTNER":
        return intl.formatMessage({ defaultMessage: "My non-married partner" })
      case "BIRTH_PARENT":
        return intl.formatMessage({ defaultMessage: "Birth parent for adoption" })
      case "DONOR":
        return intl.formatMessage({ defaultMessage: "Donor" })
      case "GESTATIONAL_CARRIER":
        return intl.formatMessage({ defaultMessage: "Gestational carrier" })
      case "PARTNER":
        return intl.formatMessage({ defaultMessage: "Partner" })
      default:
        return transaction.whoReceivedCare
    }
  }

  const getJourneyTypeLabel = () => {
    switch (transaction.journeyType) {
      case JourneyTypes.ADOPTION:
        return intl.formatMessage({ defaultMessage: "Adoption" })
      case JourneyTypes.GC:
        return intl.formatMessage({ defaultMessage: "Gestational carrier" })
      case JourneyTypes.PRESERVATION:
        return intl.formatMessage({ defaultMessage: "Fertility preservation" })
      case JourneyTypes.FERTILITY_CARE:
        return intl.formatMessage({ defaultMessage: "Fertility care" })
      case JourneyTypes.PREGNANCY_AND_POSTPARTUM_SERVICES:
        return intl.formatMessage({ defaultMessage: "Pregnancy and postpartum services" })
      case JourneyTypes.MENOPAUSE:
        return intl.formatMessage({ defaultMessage: "Menopause" })
      case JourneyTypes.LOW_TESTOSTERONE:
        return intl.formatMessage({ defaultMessage: "Low testosterone" })
      case JourneyTypes.GENDER_AFFIRMING_CARE:
        return intl.formatMessage({ defaultMessage: "Gender-affirming care" })
      case JourneyTypes.PATHFINDING:
        return intl.formatMessage({ defaultMessage: "Pathfinding" })
      case JourneyTypes.METABOLIC_FERTILITY_HEALTH:
        return intl.formatMessage({ defaultMessage: "Metabolic-fertility health" })
      case JourneyTypes.IVF:
        return intl.formatMessage({ defaultMessage: "IVF" })
      case JourneyTypes.IUI:
        return intl.formatMessage({ defaultMessage: "IUI" })
      default:
        return transaction.journeyType
    }
  }

  const getStorageFeesLabel = () => {
    switch (transaction.relatedToStorageFeesAnswer) {
      case "OTHER":
        return intl.formatMessage({ defaultMessage: "No" })
      case "FIRST_YEAR_STORAGE":
        return intl.formatMessage({ defaultMessage: "Yes: First year of storage" })
      case "POST_FIRST_YEAR_STORAGE":
        return intl.formatMessage({ defaultMessage: "Yes: After first year of storage" })
      default:
        return transaction.relatedToStorageFeesAnswer
    }
  }

  const formatCurrency = (value: number) =>
    intl.formatNumber(value / 100, {
      style: "currency",
      currency: transaction.currencyCode,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

  const getExpenseTypeLabel = () => {
    if (isOopExpense) return intl.formatMessage({ defaultMessage: "Reimbursement" })
    if (isCardExpense) return intl.formatMessage({ defaultMessage: "Carrot Card" })
    if (isDirectPayExpense) return intl.formatMessage({ defaultMessage: "Direct pay" })
    return null
  }

  const matchingSubsidy = subsidies?.find(
    (s) => s.reimbursementCategoryNameMessageKey === transaction.expensePlanCategoryNameMessageKey
  )
  const defaultReimbursementPercent = matchingSubsidy?.reimbursementRules.find(
    (rule) => rule.qmeState === QmeState.ANY && rule.outOfPocketMaxState === OutOfPocketMaxState.ANY
  )?.reimbursementPercent
  const memberCostSharePercentage = defaultReimbursementPercent !== undefined ? 100 - defaultReimbursementPercent : null

  // Direct pay + CREDIT plan only. creditsConsumed stays null until the expense is
  // scheduled/approved (e.g. an "Other" expense in review), so we show "Pending" until then.
  const isCreditPlanExpense = isDirectPayExpense && transaction.benefitType === BenefitType.CREDIT

  // Post-approval adjustment data (pay-less via the standard adjustment chain, or pay-more via a
  // linked refund) reflects the actual, current amounts and should take priority over the
  // pre-swipe preAuth estimate below.
  const hasCardAdjustment = isCardExpense && !!transaction.adjustmentDatetime && transaction.originalAmount != null

  const preAuthBreakdown =
    transaction.preAuthTotalAmount != null && transaction.preAuthReAmount != null
      ? {
          total: transaction.preAuthTotalAmount,
          carrotCard: transaction.preAuthReAmount,
          personalPayment: transaction.preAuthTotalAmount - transaction.preAuthReAmount,
          costShare: transaction.preAuthAmountAppliedToCostShare ?? 0,
          amountOverAvailableFunds:
            transaction.preAuthTotalAmount -
            transaction.preAuthReAmount -
            (transaction.preAuthAmountAppliedToCostShare ?? 0)
        }
      : null

  const FileLinks = () => {
    return (
      <>
        {transaction.fileGuids.map((guid, i) => (
          <Box paddingBottom={theme.tokens.spacing.md} key={i}>
            <Link component="button" onClick={getFileOnClick(guid)}>
              {transaction.fileNames[i]}
            </Link>
          </Box>
        ))}
      </>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={(theme) => ({
        "& .MuiDrawer-paper": {
          inlineSize: { xs: "100%", sm: "400px" },
          padding: theme.tokens.spacing.lg,
          display: "flex",
          flexDirection: "column",
          blockSize: "100%"
        }
      })}
    >
      {/* Header - Fixed at top */}
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={theme.tokens.spacing.lg}>
        <Typography variant="h4" component="h2" color="secondary">
          <FormattedMessage defaultMessage="Expense details" />
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label={intl.formatMessage({ defaultMessage: "Close expense details" })}
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.text.primary
            }
          })}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflowY: "auto" }}>
        {shouldShowOopCardContent && (
          <Stack spacing={theme.tokens.spacing.lg}>
            {/* Type */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Type" />
              </Typography>
              <Typography variant="body1">{getExpenseTypeLabel()}</Typography>
            </Box>

            {/* Merchant - Card expenses only */}
            {isCardExpense && transaction.merchantName && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Merchant" />
                </Typography>
                <Typography variant="body1">{transaction.merchantName}</Typography>
              </Box>
            )}

            {showReconciliationAlert && (
              <Alert severity="warning">
                <FormattedMessage
                  defaultMessage="You owe a reconciliation payment. Please <link>make a payment</link> to unlock your Carrot Card."
                  values={{
                    link: (linkContent) => (
                      <Link color="inherit" href={openInvoice.invoicePaymentUrl} target="_blank">
                        {linkContent}
                      </Link>
                    )
                  }}
                />
              </Alert>
            )}

            {/* History */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="History" />
              </Typography>
              {isOopExpense ? (
                <OopHistoryEntries transaction={transaction} invoices={invoices} />
              ) : (
                <CardHistoryEntries transaction={transaction} invoices={invoices} />
              )}
            </Box>

            {/* Amount */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Amount" />
              </Typography>
              {isOopExpense ? (
                <Stack spacing={theme.tokens.spacing.xs}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1">
                      <FormattedMessage defaultMessage="Approved" />
                    </Typography>
                    <Typography variant="body1">{formatCurrency(transaction.eligibleAmount)}</Typography>
                  </Box>
                  {transaction.amountAppliedToDeductible > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1">
                        <FormattedMessage defaultMessage="Deductible" />
                      </Typography>
                      <Typography variant="body1">{formatCurrency(-transaction.amountAppliedToDeductible)}</Typography>
                    </Box>
                  )}
                  {transaction.amountAppliedToCostShare > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1">
                        <FormattedMessage defaultMessage="Cost share" />
                      </Typography>
                      <Typography variant="body1">{formatCurrency(-transaction.amountAppliedToCostShare)}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1" fontWeight="bold">
                      <FormattedMessage defaultMessage="Reimbursement" />
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(transaction.amount)}
                    </Typography>
                  </Box>
                </Stack>
              ) : hasCardAdjustment ? (
                <CardAmounts transaction={transaction} formatCurrency={formatCurrency} />
              ) : preAuthBreakdown ? (
                <>
                  <Stack spacing={theme.tokens.spacing.xs}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1">
                        <FormattedMessage defaultMessage="Carrot Card" />
                      </Typography>
                      <Typography variant="body1">{formatCurrency(preAuthBreakdown.carrotCard)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1">
                        <FormattedMessage defaultMessage="Personal payment" />
                      </Typography>
                      <Typography variant="body1">{formatCurrency(preAuthBreakdown.personalPayment)}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body1" fontWeight="bold">
                        <FormattedMessage defaultMessage="Total" />
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formatCurrency(preAuthBreakdown.total)}
                      </Typography>
                    </Box>
                  </Stack>
                  {((memberCostSharePercentage !== null && preAuthBreakdown.costShare > 0) ||
                    preAuthBreakdown.amountOverAvailableFunds > 0) && (
                    <Box marginTop={theme.tokens.spacing.sm}>
                      <Typography variant="body2">
                        <FormattedMessage defaultMessage="Your personal payment includes:" />
                      </Typography>
                      <UnorderedList disablePadding={true}>
                        {memberCostSharePercentage !== null && preAuthBreakdown.costShare > 0 && (
                          <UnorderedListItem disablePadding={true}>
                            <ListItemText
                              primary={intl.formatMessage(
                                {
                                  defaultMessage: "Your {percentage}% cost share: {costShareAmount}"
                                },
                                {
                                  percentage: memberCostSharePercentage,
                                  costShareAmount: formatCurrency(preAuthBreakdown.costShare)
                                }
                              )}
                            />
                          </UnorderedListItem>
                        )}
                        {preAuthBreakdown.amountOverAvailableFunds > 0 && (
                          <UnorderedListItem disablePadding={true}>
                            <ListItemText
                              primary={intl.formatMessage(
                                {
                                  defaultMessage: "Amount above available funds: {amountOver}"
                                },
                                {
                                  amountOver: formatCurrency(preAuthBreakdown.amountOverAvailableFunds)
                                }
                              )}
                            />
                          </UnorderedListItem>
                        )}
                      </UnorderedList>
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body1">{formatCurrency(transaction.amount)}</Typography>
              )}
            </Box>

            {/* Your responses */}
            {(transaction.fileGuids?.length > 0 ||
              transaction.whoReceivedCare ||
              transaction.journeyType ||
              transaction.relatedToStorageFeesAnswer ||
              transaction.description) && (
              <Typography variant="h5" component="h3" color="secondary">
                <FormattedMessage defaultMessage="Your responses" />
              </Typography>
            )}

            {/* Documents */}
            {transaction.fileGuids?.length > 0 && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Documents" />
                </Typography>
                <FileLinks />
              </Box>
            )}

            {/* Who received care or services? */}
            {transaction.whoReceivedCare && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Who received care or services?" />
                </Typography>
                <Typography variant="body1">{getWhoReceivedCare()}</Typography>
              </Box>
            )}

            {/* What is this expense related to? */}
            {transaction.journeyType && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="What is this expense related to?" />
                </Typography>
                <Typography variant="body1">{getJourneyTypeLabel()}</Typography>
              </Box>
            )}

            {/* Is this related to storage fees? */}
            {transaction.relatedToStorageFeesAnswer && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Is this related to storage fees?" />
                </Typography>
                <Typography variant="body1">{getStorageFeesLabel()}</Typography>
              </Box>
            )}

            {/* Additional information */}
            {transaction.description && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Additional information" />
                </Typography>
                <Typography variant="body1">{transaction.description}</Typography>
              </Box>
            )}
          </Stack>
        )}

        {shouldShowFullContent && (
          <Stack spacing={theme.tokens.spacing.lg}>
            {/* Type */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Type" />
              </Typography>
              <Typography variant="body1">{getExpenseTypeLabel()}</Typography>
            </Box>

            {/* History */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="History" />
              </Typography>
              <DirectPayHistoryEntries transaction={transaction} />
            </Box>

            {/* Credits used - credit-based plans only */}
            {isCreditPlanExpense && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Credits used" />
                </Typography>
                <Typography variant="body1">
                  {transaction.creditsConsumed == null ? (
                    <FormattedMessage defaultMessage="Pending" />
                  ) : (
                    <FormattedMessage
                      defaultMessage="{count, plural, one {# credit} other {# credits}}"
                      values={{ count: transaction.creditsConsumed }}
                    />
                  )}
                </Typography>
              </Box>
            )}

            <DirectPayAmounts transaction={transaction} formatCurrency={formatCurrency} />

            {/* Service Start Date */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Service start date" />
              </Typography>
              <Typography variant="body1">
                <FormattedDate value={serviceDateStringToDate} year="numeric" month="long" day="numeric" />
              </Typography>
            </Box>

            {/* Who Received Care */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Care recipient" />
              </Typography>
              <Typography variant="body1">{getWhoReceivedCare()}</Typography>
            </Box>

            {/* Service */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Service" />
              </Typography>
              <Typography variant="body1">{transaction.directPayServiceName}</Typography>
            </Box>

            {/* Provider */}
            {transaction.providerName && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Provider" />
                </Typography>
                <Typography variant="body1">{transaction.providerName}</Typography>
              </Box>
            )}

            {/* Infertility diagnosis */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Infertility diagnosis" />
              </Typography>
              <Typography variant="body1">
                {transaction.isInfertilityFlag ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            </Box>

            {/* Medical necessity */}
            <Box>
              <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                <FormattedMessage defaultMessage="Medical necessity" />
              </Typography>
              <Typography variant="body1">
                {transaction.isMedicalNecessityFlag ? (
                  <FormattedMessage defaultMessage="Yes" />
                ) : (
                  <FormattedMessage defaultMessage="No" />
                )}
              </Typography>
            </Box>

            {/* Attachments */}
            {!transaction.isBenefitTransfer && (
              <Box>
                <Typography variant="body1" fontWeight="bold" color="secondary" marginBottom={theme.tokens.spacing.xs}>
                  <FormattedMessage defaultMessage="Documents" />
                </Typography>
                <FileLinks />
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ marginBlockStart: theme.tokens.spacing.lg }}>
        <Divider sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })} />
        <Box display="flex" justifyContent="flex-end">
          <Button
            color="secondary"
            variant="outlined"
            onClick={onClose}
            sx={() => ({
              minInlineSize: "120px"
            })}
          >
            <FormattedMessage defaultMessage="Close" />
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}
